import React, { useCallback, useState, ChangeEvent } from 'react';
import Button from '@atlaskit/button/new';
import TextField from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ApplySyncPlanRequest } from '@atlassiansox/phobos-client-ts';
import { useParams } from 'react-router-dom';

import { flexStart, spaceGapCompact, spaceTopCozy } from '@/styles';
import { apis, apiResolve } from '@/services';
// import { useFetchErrorController } from '@/controllers';
import { Alert } from '@/components/composed/modals';

type ActionBarProps = {
  name: string;
  hash: string;
  sloGroupAutoSyncEnabled: boolean;
};

const ActionBar: React.FC<ActionBarProps> = ({ name, hash, sloGroupAutoSyncEnabled }) => {
  const { publicId: publicIdParam } = useParams<{ publicId: string }>();
  const publicId = publicIdParam ?? '';

  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingCompleted, setIsSyncingCompleted] = useState(false);

  const disableManualSync = sloGroupAutoSyncEnabled;
  // const { onError } = useFetchErrorController();

  const handleVerify = useCallback(() => {
    setIsEditing(true);
  }, []);

  const handleTextFieldChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.currentTarget;
      setIsConfirmEnabled(value === name);
    },
    [name]
  );

  const queryClient = useQueryClient();
  const mutation = useMutation(
    (request: ApplySyncPlanRequest) => apiResolve(apis.slogroup.applySyncPlan(request))
    /*
    // Until we have a proper way to infer the success of the sync operation
    {
      onError,
      onSuccess: async () => {
        queryClient.invalidateQueries({ queryKey: ['slo-group', 'sync', publicId] });
        await Alert({
          title: 'Info',
          body: <>Successfully synced all the operations</>,
        });
        // These are probably not needed since after invalidating the query, the
        // component will re-render with the new view (showing a nothing to sync
        // panel)
        setIsSyncing(false);
        setIsSyncingCompleted(true);
      },
    } */
  );

  const handleConfirm = useCallback(async () => {
    setIsSyncing(true);
    setIsConfirmEnabled(false);

    await mutation.mutate({
      publicId,
      hash,
    });

    await Alert({
      title: 'Info',
      body: <>Operations syncing has been triggered...</>,
    });

    // We currently don't have a way to infer the success of the sync operation.
    // For now, we just disable the action form to avoid user keeping retrying
    setIsSyncing(false);
    setIsSyncingCompleted(true);

    // refresh the sync panel UI
    await queryClient.invalidateQueries({
      queryKey: ['slo-group', 'sync', publicId],
    });
  }, [mutation, publicId, hash, queryClient]);

  if (isSyncingCompleted) return null;

  const sectionTitle = disableManualSync ? 'Pending Sync Operations' : 'Sync Operations Required';

  return (
    <div css={spaceTopCozy}>
      <SectionMessage title={sectionTitle} appearance="warning">
        <p>
          Below is a list of pending sync operations resulting from the server cohort changes.
          {disableManualSync ? (
            <>
              &nbsp;These operations will be processed automatically as auto sync is enabled for
              your SLO Group. You can disable auto sync in your SLO Group Terraform configuration.
            </>
          ) : (
            <>&nbsp;Please verify and confirm to sync them to the SLOs.</>
          )}
        </p>
        <div key={1} css={spaceTopCozy}>
          {isEditing && (
            <div css={[flexStart, spaceGapCompact]}>
              <TextField
                width={400}
                isCompact
                placeholder="Please enter SLO name here.."
                onChange={handleTextFieldChange}
              />
              <Button onClick={handleConfirm} appearance="primary" isDisabled={!isConfirmEnabled}>
                {/* eslint-disable-next-line no-nested-ternary */}
                {isSyncingCompleted ? 'Sync Completed' : isSyncing ? 'Syncing...' : 'Confirm Sync'}
              </Button>
            </div>
          )}
          {!isEditing && (
            <Button isDisabled={disableManualSync} onClick={handleVerify} appearance="primary">
              Verify & Sync
            </Button>
          )}
        </div>
      </SectionMessage>
    </div>
  );
};

export default ActionBar;
